import type { DeviceConditionUI, DeviceTriggerUI } from 'src/types/DeviceCondAndTriggerUI';
import { OPERATORS } from 'src/constants';
import { shellyId16 } from 'src/helpers/interface_helpers';
import {
	extractGuiDeviceId,
	convertSceneCommandToUiProperty,
	convertSceneWeatherToUi,
} from 'src/components/Scenes/helpers/utilities_scenes_to_ui';
import { _init_time_based_conditions } from 'src/components/Scenes/helpers/init_time_conditions';
import type { TimeBasedConditionsUI } from 'src/types/TimeBasedConditionsUI';

import type {
	DeviceAnyChangeTrigger,
	DeviceConditionOperatorAsKey,
	DeviceConditionOperatorInR,
	DeviceOnceTrigger,
	DeviceRepeatTrigger,
	SceneAsTrigger,
	SunriseSunsetTrigger,
	WeatherTriggerOnAnyChange,
	WeatherOnceTrigger,
	EnergyExchangeTrigger,
	MathCondition,
	ManualExecutionCondition,
	ManualExecutionConditionUI,
	SceneConditionTransform,
} from 'src/types/ScenesConditionsTypes';
import {
	is_weather_on_any_change_trigger,
	is_weather_one_time_trigger,
	is_energy_exchange_trigger,
	is_device_cond_op_as_key,
	is_device_cond_op_in_r,
	is_device_once_trigger,
	is_device_on_any_change_trigger,
	is_repeat_device_trigger,
	is_scene_as_trigger,
	is_sunrise_sunset_trigger,
	is_math_condition,
} from 'src/types/ScenesConditionsTypes';
import type { AutomationConditionUI } from 'src/types/AutomationConditionUI';
import type { SunriseSunsetConditionUI } from 'src/types/SunriseSunsetConditionUI';
import type { WeatherConditionUI } from 'src/types/WeatherConditionUI';
import type { EnergyExchangeConditionUI } from 'src/types/EnergyExchangeConditionUI';
import type { MathConditionUI } from 'src/types/Scenes/MathConditionUI';

import { _ } from 'svelte-i18n';
import { type DeviceID } from 'src/types/DeviceID';

const refFromCond = (cond: { r?: string; rr?: string }): string | undefined =>
	typeof cond?.r === 'string' ? cond.r : typeof cond?.rr === 'string' ? cond.rr : undefined;

export const is_channel_only_condition = (cond: { r?: string; rr?: string }): boolean =>
	(refFromCond(cond) ?? '').endsWith(':channel');

export const deviceConditionSecondFormat = (cond: DeviceConditionOperatorInR): DeviceConditionUI => {
	const operator = cond.cmpop;
	const value = cond.cmpval;
	const tr_reason = refFromCond(cond) || '';
	const transform = cond.transform;

	let [reason = '', backendDeviceID = '', sceneCondName = ''] =
		tr_reason !== '' ? tr_reason.split(':') : [];

	/**
	 * convertSceneCommandToUiProperty parses condition name and threshold value to UI friendly format,
	 * including
	 * - condition name (for UI purpose)
	 * - device channel (extract from scene condition name)
	 * - desired value
	 * - input_index - optional -specific case
	 * - addOnId - optional - specific case
	 */
	let translate_cond = convertSceneCommandToUiProperty(sceneCondName, value);

	const resolvedOperator = operator;
	if (
		(sceneCondName === 'rotie1s' || sceneCondName === 'rotie1sc') &&
		translate_cond?.name === 'scroll_steps' &&
		typeof translate_cond?.value === 'number'
	) {
		translate_cond = { ...translate_cond, value: Math.abs(translate_cond.value) };
	}

	/** Note: This might not execute correctly. Should be revised if its obselete */
	//check lux not string -> number value from old UI
	if (sceneCondName === 'lux' && !isNaN(Number(value))) {
		sceneCondName = 'lux_i';
	}

	//Prepare device id for UI
	let hexDeviceId = backendDeviceID.toLocaleLowerCase().startsWith('x')
		? backendDeviceID
		: shellyId16(backendDeviceID); //parseInt(backendDeviceID).toString(16).padStart(6, '0');
	let uiDevice: DeviceID = {
		id: hexDeviceId,
		channel: translate_cond?.channel,
	};

	// Override interface device id if it is provided in the condition iots script
	if (cond?._gui_device_id) {
		uiDevice = extractGuiDeviceId(cond._gui_device_id);
	}

	// if (import.meta.env.DEV) {
	// 	console.warn(
	// 		`IoTS2UI: Device Condition 2.format [${uiDevice.id} : ${uiDevice.channel}]`,
	// 		cond,
	// 		translate_cond,
	// 	);
	// }

	const condWithChannel = cond as unknown as { _channel_from_and?: number };
	const remote_control_channel_from_and =
		translate_cond.remote_control_channel ??
		(typeof condWithChannel._channel_from_and === 'number' && condWithChannel._channel_from_and >= 0
			? `channel_${condWithChannel._channel_from_and}`
			: undefined);

	return {
		device: uiDevice,
		cond_name: translate_cond.name,
		operator: OPERATORS.find((el) => el[resolvedOperator])?.[resolvedOperator],
		value: translate_cond.value,
		add_info_ind: translate_cond.add_info_ind,
		input_index: translate_cond.input_index,
		remote_control_channel: remote_control_channel_from_and,
		transform: transform,
	};
};

//------- conditions interpretator functions
export const deviceConditionInterpretator = (
	cond:
		| DeviceOnceTrigger
		| DeviceRepeatTrigger
		| DeviceAnyChangeTrigger
		| DeviceConditionOperatorAsKey,
): DeviceTriggerUI | DeviceConditionUI => {
	const legacyOperatorMap: Record<string, string> = {
		on: 'equal',
		off: 'equal',
	};
	// if (import.meta.env.DEV)
	// 	console.warn('INTERPRETATOR: Device Condition: ', cond);

	/**
	 * cond_type_name variable used for device triggers - once/repeatedly/on any change
	 * holdOff is a valid interval in minutes for ONCE condition (other don't support it)
	 */
	let cond_type_name = '',
		holdOff = undefined;

	/**
	 * Condition operator eq (equal), lt (less than), mt (more than)
	 */
	const operator = is_device_once_trigger(cond)
		? cond.thsop
		: Object.keys(cond).find((key) => OPERATORS.some((op) => Object.keys(op).includes(key)));

	/**
	 * rr stand for condition as trigger for new scenes it would have only r
	 * both of them contain the dev id and selected condition by channel index of element -ex: ext temp sensor
	 */

	let tr_reason = '',
		tshvalue: string | number = '',
		transform: SceneConditionTransform | undefined = undefined;
	if (is_device_cond_op_as_key(cond)) {
		const rObj = cond[operator].find((el) => el.r);
		tr_reason = rObj?.r || '';
		transform = rObj?.transform;
		tshvalue = cond[operator].find((el) => !el.r && el !== undefined) || '';
	} else if (is_device_once_trigger(cond)) {
		tr_reason = cond?.rr || '';
		transform = cond?.transform;
		tshvalue = typeof cond.thsval !== 'undefined' ? cond.thsval : '';
	} else {
		//any change and repeat triggers
		const rrObj = cond[operator].find((el) => el?.rr);
		tr_reason = rrObj?.rr || '';
		transform = rrObj?.transform;
		tshvalue = cond[operator].find((el) => !el?.rr && typeof el !== 'undefined') || '';
	}

	/** Extract
	 * reason (type of if trigger/condition )  rr -> trigger, r -> condition,
	 * device id in decimal format and
	 */
	let [reason = '', backendDeviceID = '', sceneCondName = ''] =
		tr_reason !== '' ? tr_reason.split(':') : [];

	/**
	 * convertSceneCommandToUiProperty parses condition name and threshold value to UI friendly format,
	 * including
	 * - condition name (for UI purpose)
	 * - device channel (extract from scene condition name)
	 * - desired value
	 * - input_index - optional -specific case
	 * - addOnId - optional - specific case
	 *
	 */
	let translate_cond = convertSceneCommandToUiProperty(sceneCondName, tshvalue);

	const resolvedOperator = operator;
	if (
		(sceneCondName === 'rotie1s' || sceneCondName === 'rotie1sc') &&
		translate_cond?.name === 'scroll_steps' &&
		typeof translate_cond?.value === 'number'
	) {
		translate_cond = { ...translate_cond, value: Math.abs(translate_cond.value) };
	}

	/** Note: This might not execute correctly. Should be revised if its obselete */
	//check lux not string -> number value from old UI
	if (sceneCondName === 'lux' && !isNaN(Number(tshvalue))) {
		sceneCondName = 'lux_i';
	}

	//Prepare device id for UI
	let hexDeviceId = backendDeviceID.toLocaleLowerCase().startsWith('x')
		? backendDeviceID
		: shellyId16(backendDeviceID); //parseInt(backendDeviceID).toString(16).padStart(6, '0');
	let uiDevice: DeviceID = {
		id: hexDeviceId,
		channel: translate_cond?.channel,
	};

	// Override interface device id if it is provided in the condition iots script
	if (cond?._gui_device_id) {
		uiDevice = extractGuiDeviceId(cond._gui_device_id);
	}

	if (is_device_once_trigger(cond)) {
		cond_type_name = 'once';
		holdOff = cond.hold_off || 0;
	} else if (is_repeat_device_trigger(cond)) {
		cond_type_name = 'repeat';
	} else if (is_device_on_any_change_trigger(cond)) {
		cond_type_name = 'any';
	} else {
		cond_type_name = undefined;
	}

	// if (import.meta.env.DEV) {
	// 	console.warn(
	// 		`IoTS2UI: Device Condition [${uiDevice.id} : ${uiDevice.channel}]`,
	// 		cond,
	// 		translate_cond,
	// 	);
	// }

	const condWithChannelInterp = cond as unknown as { _channel_from_and?: number };
	const remote_control_channel_interpretator =
		translate_cond.remote_control_channel ??
		(typeof condWithChannelInterp._channel_from_and === 'number' &&
		condWithChannelInterp._channel_from_and >= 0
			? `channel_${condWithChannelInterp._channel_from_and}`
			: undefined);

	return {
		device: uiDevice,
		cond_name: translate_cond.name,
		operator: OPERATORS.find((el) => el[resolvedOperator])?.[resolvedOperator],
		value: translate_cond.value,
		cond_type:
			cond_type_name !== undefined
				? {
						cond_type_name,
						valid_for_min: holdOff,
					}
				: undefined,
		add_info_ind: translate_cond.add_info_ind,
		input_index: translate_cond.input_index,
		remote_control_channel: remote_control_channel_interpretator,
		transform: transform,
	};
};

/** Daily condition without selected days
 *  { "eq":[{ "rr":"dt:t" }, "00:00"], "_gui_type":"d_cond"}

Daily condition with selected days
  { "and":[ { "eq":[ { "rr":"dt:t" }, "00:00"] },
			{ "wday":"now", "isin":"1 2 3 4 5 6 "}
		],
	"_gui_type":"d_cond"
} */
export const dailyConditionInterpretator = (cond) => {
	const isin = cond?.and
		?.find((el) => typeof el === 'object' && el.isin !== undefined)
		?.isin?.trim()
		?.split(' ')
		?.map((el) => parseInt(el) - 1) ?? [0, 1, 2, 3, 4, 5, 6];

	const time = cond?.and
		? cond.and
				.find((el) => el.eq !== undefined)
				?.eq?.find((el) => el?.rr === undefined && typeof el === 'string')
		: cond?.eq?.find((el) => el?.rr === undefined && typeof el === 'string');

	const id = _init_time_based_conditions.findIndex((el) => el.props._gui_type === cond._gui_type);
	return { value: { isin, time }, id, _gui_type: cond._gui_type };
};

/** Once executed with date and time
 *  { "eq":[ { "rr":"dt:dt" }, "2021-06-11 00:00" ],
	"_gui_type":"ot_cond" } 
*/
export const oneTimeConditionInterpretator = (cond) => {
	const value: string | undefined = cond?.eq?.find(
		(el) => el?.rr === undefined && typeof el === 'string',
	);
	const [date, time] = value?.split(' ') ?? ['', ''];
	const id = _init_time_based_conditions.findIndex((el) => el.props._gui_type === cond._gui_type);
	/**required format for edit condition value: {date, time}*/
	return { value: { date, time }, id, _gui_type: cond._gui_type };
};

/** Timer Condition Format
 *  { "dminslimit":"1",
	"rrtrig":{ "rr":"dt:t" },
	"_gui_type":"timer"}
 */
export const timerConditionInterpretator = (cond) => {
	let value = cond.dminslimit || 0;
	let id = _init_time_based_conditions.findIndex((el) => el.props._gui_type === cond._gui_type);

	return { value, id, _gui_type: cond._gui_type };
};

export const automationRuns = (cond: SceneAsTrigger) => {
	let rr = cond?.rr;
	let [automation = 'nt', on_run_or_on_exe = '', scene_id = ''] = rr.split(':');

	return {
		scene_id,
		on_run_or_on_exe,
	};
};

const extract_hours_min_from_dur = (dur: string) => {
	let _dur: number = !isNaN(Number(dur)) ? Number(dur) : 0;
	let hours: number = _dur === 60 ? 1 : Math.floor(_dur / 60) || 0;
	let mins: number = _dur % 60 || 0;
	return {
		hours: hours >= 0 ? hours : 0,
		mins: mins >= 0 ? mins : 0,
	};
};

export const sunriseSunsetInterpretator = (cond: SunriseSunsetTrigger): SunriseSunsetConditionUI => {
	let rr = cond?.rr;
	let [sun = 'sun', locid = '', riseOrSetWithDur = 'set+0'] = rr.split(':');
	let mode = riseOrSetWithDur.match(/rise/) ? 'rise' : 'set';
	let dur = riseOrSetWithDur.split(mode)[1];
	let beforeOrAfter = (dur.startsWith('+') ? 'after' : 'before') as 'before' | 'after';
	let type: 'sunrise' | 'sunset' = `${sun}${mode}` as 'sunrise' | 'sunset';
	let { hours, mins } = extract_hours_min_from_dur(dur.substring(1));

	return {
		type,
		beforeAfter: beforeOrAfter,
		hours,
		mins,
		locid,
	};
};

/** *	{ eq: [ {"rr":"owm:<locid>+<hour offset>:<cond>"}, value ] } */
export const weatherOnAnyChangeInterpretator = (
	_condition: WeatherTriggerOnAnyChange,
): WeatherConditionUI => {
	let [operator = 'eq', rr_owm = [{ rr: '' }, -1]] = Object.entries(_condition)[0];

	let op = OPERATORS.find((el) => el[operator])?.[operator as 'eq' | 'lt' | 'gt'];
	let owm_cond = rr_owm[0]?.rr || '';
	let thsval = rr_owm[1];
	let [owm = 'owm', locid_plus_hours = '', weather_cond = ''] = owm_cond.split(':');

	let { cond, value } = convertSceneWeatherToUi(weather_cond, thsval);
	let [locid = '', hours = 0] = locid_plus_hours.split('+');

	return {
		cond,
		value,
		op,
		locid,
		hours: Number(hours),
		execution_type: 'any',
		thsval, //needed this for invalid weather labels
	};
};

export const weatherOneTimeInterpretator = (_condition: WeatherOnceTrigger): WeatherConditionUI => {
	let operator = _condition.thsop;
	let val = _condition.thsval;

	let op = OPERATORS.find((el) => el[operator])?.[operator as 'eq' | 'lt' | 'gt'];
	let [owm = 'owm', locid_plus_hours = '', weather_cond = ''] = _condition?.rr?.split(':');

	let { cond, value } = convertSceneWeatherToUi(weather_cond, val);
	let [locid = '', hours = 0] = locid_plus_hours.split('+');
	return {
		cond,
		value,
		op,
		locid,
		hours: Number(hours),
		execution_type: 'once',
		thsval: val, //needed this for invalid weather labels
	};
};
/*** {
 * "lt": [ { "rr": "ppp:ibex:0" },	180],
 * "_gui_purpose": "condition",
 * "rrtrig": {"rr": "dt:t"}
 * }
 */

export const energyExchangeInterpretator = (
	_condition: EnergyExchangeTrigger,
): EnergyExchangeConditionUI => {
	let [operator = 'eq', rr_ppp = [{ rr: '' }, -1]] = Object.entries(_condition)[0];

	let op = OPERATORS.find((el) => el[operator])?.[operator as 'eq' | 'lt' | 'gt'];
	let [sceneCond = '', energy_market = '', minutes = '0'] = rr_ppp[0]?.rr?.split(':');
	let value = Number(rr_ppp[1]);

	// use getCurrencyToMWh() when we get dynamic api with different currencies.
	// let character: string = getCurrencyToMWh(energy_market)
	let character: string = 'EUR/MWh';

	return {
		cond_name: sceneCond === 'ppp' ? 'energy_exchange' : '',
		value: value,
		operator: op,
		exchange_market: energy_market,
		character: character,
		minutes: Number(minutes),
	};
};

/**
 * Math condition from iotS script
 * @param cond  iots script for math condition
 * @returns <MathConditionUI>	UI object for math condition
 */
export const mathConditionInterpretator = (cond: MathCondition): MathConditionUI => {
	// if (import.meta.env.DEV) {
	// 	console.log('Math Condition Interpretator: ', cond);
	// }
	return {
		math_script: {}, //Empty to be generated by the math lib on change
		expression: cond._gui_math_expression || '',
		params: cond._gui_math_params || [],
		_gui_type: 'math_based',
	};
};

/**
 * Manual execution condition from iotS script
 * @param cond iots condition
 * @returns UI object for previewing manual execution condition
 */
export const manualExecutionConditionInterpretator = (
	cond: ManualExecutionCondition,
): ManualExecutionConditionUI => {
	return {
		...cond,
	};
};

export const conditionInterpretator = (
	cond,
	purpose,
):
	| DeviceTriggerUI
	| TimeBasedConditionsUI
	| DeviceConditionUI
	| AutomationConditionUI
	| SunriseSunsetConditionUI
	| WeatherConditionUI
	| EnergyExchangeConditionUI
	| MathConditionUI
	| ManualExecutionConditionUI
	| undefined => {
	let tr:
		| DeviceConditionUI
		| DeviceTriggerUI
		| TimeBasedConditionsUI
		| AutomationConditionUI
		| SunriseSunsetConditionUI
		| WeatherConditionUI
		| EnergyExchangeConditionUI
		| MathConditionUI
		| ManualExecutionConditionUI
		| undefined;

	// if(import.meta.env.DEV) {
	// 	console.log('Scene Condition Interpretator: ', cond, purpose);
	// }

	switch (purpose) {
		case 'manual_execution':
			tr = <ManualExecutionConditionUI>{ ...cond };
			break;
		case 'math_based':
			tr = <MathConditionUI>{ ...mathConditionInterpretator(cond) };
			break;
		case 'condition':
			if (is_weather_on_any_change_trigger(cond)) {
				tr = <WeatherConditionUI>{ ...weatherOnAnyChangeInterpretator(cond) };
			} else if (is_weather_one_time_trigger(cond)) {
				tr = <WeatherConditionUI>{ ...weatherOneTimeInterpretator(cond) };
			} else if (is_energy_exchange_trigger(cond)) {
				tr = <EnergyExchangeConditionUI>{
					...energyExchangeInterpretator(cond),
				};
			} else if (is_device_cond_op_in_r(cond)) {
				tr = <DeviceConditionUI>{ ...deviceConditionSecondFormat(cond) };
			} else if (is_device_cond_op_as_key(cond)) {
				tr = <DeviceConditionUI>{ ...deviceConditionInterpretator(cond) };
			} else if (
				is_device_on_any_change_trigger(cond) ||
				is_device_once_trigger(cond) ||
				is_repeat_device_trigger(cond)
			) {
				tr = <DeviceTriggerUI>{ ...deviceConditionInterpretator(cond) };
			} else {
				console.warn('Condition Interpretator: Unknown condition: ', cond);
			}

			break;
		case 'd_cond':
			tr = <TimeBasedConditionsUI>{ ...dailyConditionInterpretator(cond) };
			break;
		case 'ot_cond':
			tr = <TimeBasedConditionsUI>{ ...oneTimeConditionInterpretator(cond) };
			break;

		case 'timer':
			tr = <TimeBasedConditionsUI>{ ...timerConditionInterpretator(cond) };
			break;

		default:
			// conditions without _gui_purpose ot _gui_type
			if (is_device_cond_op_in_r(cond)) {
				//we do the device condition only (it is the second format for scene service for this option)
				tr = <DeviceConditionUI>{ ...deviceConditionSecondFormat(cond) };
			}
			if (is_scene_as_trigger(cond)) {
				tr = <AutomationConditionUI>automationRuns(cond);
				// console.log('translated ', tr);
			}
			if (is_sunrise_sunset_trigger(cond)) {
				tr = <SunriseSunsetConditionUI>{ ...sunriseSunsetInterpretator(cond) };
			}

			break;
	}

	// if (import.meta.env.DEV) {
	// 	console.log('Condition Interpreter result: ', tr);
	// }

	return tr;
};
// ------ end condition interpretator ---------
